import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Save, Trash2, Monitor, Copy, ClipboardPaste, Undo2, Redo2,
  ChevronsUp, ChevronsDown, ChevronUp, ChevronDown, ChevronLeft, ChevronRight,
  ChevronsLeft, ChevronsRight,
  RotateCcw, RotateCw, RefreshCw, Play, StopCircle, Plus,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  HMIComponent, HMIComponentType, HMILayout, SavedHMILayout,
  CANVAS_W, CANVAS_H, GRID, COMPONENT_DEFAULTS, PALETTE_ITEMS, FLUID_TYPES,
  renderHMIComponent, resolveFlowSourceOn, resolveFlowSourceIntensity,
} from './HMIComponents';
import { API_BASE } from '../../utils/api-base';
import { sessionStorage as authSession } from '../../utils/auth-api';

// ===================== HELPERS =====================

// Same grouping used in PLCsTab.tsx / EnergyMetersTab.tsx, so unit choices stay
// consistent across the app instead of every screen inventing its own list.
const COMMON_HMI_UNITS = [
  { group: 'Electrical', units: ['V', 'mV', 'A', 'mA', 'kW', 'MW', 'kWh', 'MWh', 'kVA', 'kVAR', 'Hz', 'PF', 'Ω'] },
  { group: 'Temperature', units: ['°C', '°F', 'K'] },
  { group: 'Pressure', units: ['bar', 'mbar', 'psi', 'Pa', 'kPa', 'MPa'] },
  { group: 'Flow', units: ['L/min', 'L/h', 'm³/h', 'GPM'] },
  { group: 'Speed', units: ['RPM', 'm/s', 'km/h'] },
  { group: 'Level / Length', units: ['mm', 'cm', 'm', '%'] },
  { group: 'Weight', units: ['g', 'kg', 't'] },
  { group: 'Time', units: ['ms', 's', 'min', 'h'] },
];
const ALL_COMMON_HMI_UNITS = COMMON_HMI_UNITS.flatMap(g => g.units);

const snap = (v: number) => Math.round(v / GRID) * GRID;

function genId() {
  return `hmi_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

// Rotates a point (px,py) around a pivot (cx,cy) by `deg` degrees — used to find a
// Line/Arrow's actual on-canvas endpoint positions once `rotation` is applied, so
// dragging an endpoint can compute the correct new x/y/width/rotation directly from
// where the user drops it, instead of the user having to reconcile Width + Rotation
// fields by hand after every rotate (which is what made rotated lines/arrows feel
// like their length/position "jumped").
function rotatePoint(px: number, py: number, cx: number, cy: number, deg: number) {
  const rad = (deg * Math.PI) / 180;
  const cos = Math.cos(rad), sin = Math.sin(rad);
  const dx = px - cx, dy = py - cy;
  return { x: cx + dx * cos - dy * sin, y: cy + dx * sin + dy * cos };
}

// The two endpoints of a Line/Arrow component in actual canvas coordinates, given
// its current x/y/width/height/rotation. "start" is the local-left end (x=0),
// "end" is the local-right end (x=width) — matches how HMIComponents draws them.
function lineEndpoints(comp: HMIComponent) {
  const cx = comp.x + comp.width / 2;
  const cy = comp.y + comp.height / 2;
  const rot = comp.rotation ?? 0;
  const midY = comp.y + comp.height / 2;
  const start = rotatePoint(comp.x, midY, cx, cy, rot);
  const end = rotatePoint(comp.x + comp.width, midY, cx, cy, rot);
  return { start, end };
}

const DEFAULT_LAYOUT: HMILayout = {
  width: CANVAS_W,
  height: CANVAS_H,
  background: '#0f172a',
  gridSize: GRID,
  components: [],
};

// ===================== ICON BUTTON =====================

function IconBtn({
  onClick, title, disabled, danger, active, children,
}: {
  onClick: () => void; title?: string; disabled?: boolean; danger?: boolean; active?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      disabled={disabled}
      className={`flex items-center gap-1 px-2 py-1.5 rounded text-xs font-medium transition-colors
        ${danger
          ? 'bg-rose-600/80 hover:bg-rose-700 text-white'
          : active
          ? 'bg-blue-600 text-white'
          : 'bg-slate-700 hover:bg-slate-600 text-slate-300 hover:text-white'}
        disabled:opacity-30 disabled:cursor-not-allowed`}
    >
      {children}
    </button>
  );
}

// ===================== TAG SEARCH SELECT =====================

// Searchable combobox for picking a PLC tag to bind — plain <select> becomes
// unusable once a client has dozens/hundreds of tags across many PLCs, so this
// lets the user type any part of the tag or PLC name to filter instead of
// scrolling a giant list.
function TagSearchSelect({
  tags, value, onSelect,
}: {
  tags: { plcId: string; plcName: string; tagName: string }[];
  value: string; // "plcId::tagName" or ''
  onSelect: (plcId?: string, tagName?: string) => void;
}) {
  const fieldCls = "w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-white focus:border-blue-500 outline-none";
  const selected = tags.find(t => `${t.plcId}::${t.tagName}` === value);
  const label = (t: { plcName: string; tagName: string }) => `${t.plcName} / ${t.tagName}`;

  const [query, setQuery] = useState(selected ? label(selected) : '');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);

  // Once a tag is picked, the input shows its label verbatim — treat that as
  // "no active filter" so reopening the dropdown shows the full list again.
  const filtering = query.trim() !== '' && query !== (selected ? label(selected) : '');
  const filtered = filtering
    ? tags.filter(t => {
        const q = query.toLowerCase();
        return t.tagName.toLowerCase().includes(q) || t.plcName.toLowerCase().includes(q);
      })
    : tags;

  const commit = (t?: { plcId: string; plcName: string; tagName: string }) => {
    if (t) {
      onSelect(t.plcId, t.tagName);
      setQuery(label(t));
    } else {
      onSelect(undefined, undefined);
      setQuery('');
    }
    setOpen(false);
  };

  return (
    <div className="relative">
      <div className="flex gap-1">
        <input
          className={fieldCls}
          value={query}
          placeholder="Search tag or PLC name…"
          onFocus={() => { setOpen(true); setHighlight(0); }}
          onChange={e => { setQuery(e.target.value); setOpen(true); setHighlight(0); }}
          onKeyDown={e => {
            if (!open) return;
            if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, filtered.length - 1)); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
            else if (e.key === 'Enter') { e.preventDefault(); if (filtered[highlight]) commit(filtered[highlight]); }
            else if (e.key === 'Escape') { e.preventDefault(); setOpen(false); }
          }}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
        />
        {value && (
          <button
            type="button"
            title="Clear binding"
            onMouseDown={e => e.preventDefault()}
            onClick={() => commit(undefined)}
            className="px-2 text-xs text-slate-400 hover:text-white bg-slate-800 border border-slate-700 rounded"
          >
            ✕
          </button>
        )}
      </div>
      {open && (
        <div className="absolute z-20 mt-1 w-full max-h-48 overflow-auto bg-slate-800 border border-slate-700 rounded shadow-lg">
          {filtered.length === 0 ? (
            <div className="px-2 py-1.5 text-xs text-slate-500">No matching tags</div>
          ) : (
            filtered.map((t, i) => (
              <div
                key={`${t.plcId}::${t.tagName}`}
                onMouseDown={e => { e.preventDefault(); commit(t); }}
                onMouseEnter={() => setHighlight(i)}
                className={`px-2 py-1.5 text-xs cursor-pointer ${i === highlight ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-700'}`}
              >
                {label(t)}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ===================== PROPS PANEL =====================

interface PropsPanelProps {
  comp: HMIComponent;
  components: HMIComponent[];
  availableTags: { plcId: string; plcName: string; tagName: string }[];
  // Other saved screens (layouts) for this client, for the Nav Button's "which
  // screen does this jump to" picker — excludes the screen currently being edited.
  otherScreens: { id: string; name: string }[];
  onChange: (patch: Partial<HMIComponent>) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onRotateCW: () => void;
  onRotateCCW: () => void;
  onBringToFront: () => void;
  onSendToBack: () => void;
  onBringForward: () => void;
  onSendBackward: () => void;
}

function PropsPanel({ comp, components, availableTags, otherScreens, onChange, onDelete, onDuplicate, onRotateCW, onRotateCCW, onBringToFront, onSendToBack, onBringForward, onSendBackward }: PropsPanelProps) {
  const fieldCls = "w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-white focus:border-blue-500 outline-none";
  const labelCls = "text-[10px] text-slate-400 block mb-1 font-semibold uppercase tracking-wide";
  const sectionCls = "border-t border-slate-800 pt-3";

  const currentBinding = comp.plcId && comp.tagName ? `${comp.plcId}::${comp.tagName}` : '';

  // Remounts fresh per selected component (see `key={selectedComp.id}` at the call
  // site), so this correctly starts in "custom" mode for a component whose unit is
  // already a one-off value, without needing to sync on every prop change.
  const [unitIsCustom, setUnitIsCustom] = useState(() => !!comp.unit && !ALL_COMMON_HMI_UNITS.includes(comp.unit));

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be smaller than 5MB'); return; }
    const reader = new FileReader();
    reader.onload = () => onChange({ imageUrl: reader.result as string });
    reader.onerror = () => toast.error('Failed to read image file');
    reader.readAsDataURL(file);
  };

  return (
    <div className="p-3 space-y-3 text-white text-xs">
      {/* Component header */}
      <div className="bg-slate-800 rounded-lg p-2">
        <p className="text-[10px] text-slate-500 uppercase tracking-wider">Selected</p>
        <p className="text-sm font-bold text-blue-400 capitalize mt-0.5">{comp.type.replace('-', ' ')}</p>
      </div>

      {/* Quick actions */}
      <div className={sectionCls}>
        <p className={labelCls}>Actions</p>
        <div className="grid grid-cols-2 gap-1">
          <IconBtn onClick={onDuplicate} title="Duplicate (Ctrl+D)"><Copy className="w-3 h-3" />Duplicate</IconBtn>
          <IconBtn onClick={onDelete} title="Delete (Del)" danger><Trash2 className="w-3 h-3" />Delete</IconBtn>
        </div>
      </div>

      {/* Layer order */}
      <div className={sectionCls}>
        <p className={labelCls}>Layer Order</p>
        <div className="grid grid-cols-2 gap-1">
          <IconBtn onClick={onBringToFront} title="Bring to Front"><ChevronsUp className="w-3 h-3" />Front</IconBtn>
          <IconBtn onClick={onSendToBack} title="Send to Back"><ChevronsDown className="w-3 h-3" />Back</IconBtn>
          <IconBtn onClick={onBringForward} title="Bring Forward"><ChevronUp className="w-3 h-3" />Forward</IconBtn>
          <IconBtn onClick={onSendBackward} title="Send Backward"><ChevronDown className="w-3 h-3" />Backward</IconBtn>
        </div>
      </div>

      {/* Rotation */}
      <div className={sectionCls}>
        <p className={labelCls}>Rotation</p>
        <div className="flex items-center gap-1.5 mb-1.5">
          <IconBtn onClick={onRotateCCW} title="Rotate 45° CCW"><RotateCcw className="w-3 h-3" />-45°</IconBtn>
          <IconBtn onClick={onRotateCW} title="Rotate 45° CW"><RotateCw className="w-3 h-3" />+45°</IconBtn>
          <IconBtn onClick={() => onChange({ rotation: 0 })} title="Reset rotation"><RefreshCw className="w-3 h-3" />Reset</IconBtn>
        </div>
        <input
          type="number" step="1"
          className={fieldCls}
          value={comp.rotation ?? 0}
          onChange={e => onChange({ rotation: Number(e.target.value) % 360 })}
          placeholder="0"
        />
      </div>

      {/* Label */}
      <div className={sectionCls}>
        <label className={labelCls}>Label</label>
        <input className={fieldCls} value={comp.label}
          onChange={e => onChange({ label: e.target.value })} />
        <p className="text-[10px] text-slate-500 mt-1">Click the name on the canvas to drag it anywhere, or nudge it with the arrow keys.</p>
        {(comp.labelDx || comp.labelDy) ? (
          <button
            type="button"
            onClick={() => onChange({ labelDx: 0, labelDy: 0 })}
            className="mt-1.5 text-[10px] text-blue-400 hover:text-blue-300 font-semibold"
          >
            Reset label position
          </button>
        ) : null}
        <div className="flex items-center gap-2 mt-2">
          <input type="color" className="w-8 h-7 rounded border border-slate-700 bg-transparent cursor-pointer"
            value={comp.labelColor || '#94a3b8'}
            onChange={e => onChange({ labelColor: e.target.value })} />
          <span className="text-xs text-slate-400 font-mono">{comp.labelColor || 'default'}</span>
          {comp.labelColor && (
            <button
              type="button"
              onClick={() => onChange({ labelColor: undefined })}
              className="text-[10px] text-blue-400 hover:text-blue-300 font-semibold ml-auto"
            >
              Reset
            </button>
          )}
        </div>
        <p className="text-[10px] text-slate-500 mt-1">Text color for this name/label.</p>
        {comp.type !== 'label' && comp.type !== 'nav-button' && (
          <>
            <p className={labelCls + ' mt-2'}>Label Rotation</p>
            <div className="flex items-center gap-1.5 mb-1.5">
              <IconBtn onClick={() => onChange({ labelRotation: ((comp.labelRotation ?? 0) - 45 + 360) % 360 })} title="Rotate label 45° CCW"><RotateCcw className="w-3 h-3" />-45°</IconBtn>
              <IconBtn onClick={() => onChange({ labelRotation: ((comp.labelRotation ?? 0) + 45) % 360 })} title="Rotate label 45° CW"><RotateCw className="w-3 h-3" />+45°</IconBtn>
              <IconBtn onClick={() => onChange({ labelRotation: 0 })} title="Reset label rotation"><RefreshCw className="w-3 h-3" />Reset</IconBtn>
            </div>
            <input
              type="number" step="1"
              className={fieldCls}
              value={comp.labelRotation ?? 0}
              onChange={e => onChange({ labelRotation: Number(e.target.value) % 360 })}
              placeholder="0"
            />
            <p className="text-[10px] text-slate-500 mt-1">Spins just this label text, independent of the widget's own Rotation above.</p>
          </>
        )}
      </div>

      {/* Image upload — image only */}
      {comp.type === 'image' && (
        <div className={sectionCls}>
          <label className={labelCls}>Image</label>
          {comp.imageUrl ? (
            <div className="mb-2 rounded border border-slate-700 bg-slate-950 flex items-center justify-center overflow-hidden" style={{ height: 80 }}>
              <img src={comp.imageUrl} alt="" className="max-h-full max-w-full object-contain" />
            </div>
          ) : (
            <p className="text-[10px] text-slate-500 mb-2">No image selected yet.</p>
          )}
          <input
            type="file" accept="image/*" onChange={handleImageUpload}
            className="block w-full text-[10px] text-slate-400 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-blue-600 file:text-white file:text-[10px] file:font-semibold hover:file:bg-blue-500 cursor-pointer"
          />
          {comp.imageUrl && (
            <button type="button" onClick={() => onChange({ imageUrl: undefined })}
              className="mt-1.5 text-[10px] text-blue-400 hover:text-blue-300 font-semibold">
              Remove image
            </button>
          )}
          <label className="flex items-center gap-2 cursor-pointer mt-2">
            <input
              type="checkbox"
              checked={comp.imageFit !== 'stretch'}
              onChange={e => onChange({ imageFit: e.target.checked ? 'contain' : 'stretch' })}
              className="accent-blue-500 w-3.5 h-3.5"
            />
            <span className="text-[10px] text-slate-400">Preserve aspect ratio</span>
          </label>
        </div>
      )}

      {/* Sensor type */}
      {comp.type === 'sensor' && (
        <div>
          <label className={labelCls}>Sensor Type (PT / TT / FT / LT …)</label>
          <input className={fieldCls} value={comp.sensorType || ''}
            placeholder="PT"
            onChange={e => onChange({ sensorType: e.target.value })} />
        </div>
      )}

      {/* Level Transmitter present — tanks only */}
      {['tank', 'tank-h'].includes(comp.type) && (
        <div className={sectionCls}>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={comp.hasLevelSensor !== false}
              onChange={e => onChange({ hasLevelSensor: e.target.checked })}
              className="accent-blue-500 w-3.5 h-3.5"
            />
            <span className={labelCls + ' mb-0'}>Has Level Transmitter (LT)</span>
          </label>
          <p className="text-[10px] text-slate-500 mt-1">
            Turn off if this tank has no real LT sensor — hides the fill bar, sight glass, alarm lights, and readout instead of showing fake data.
          </p>
        </div>
      )}

      {/* Nav Button target screen — nav-button only */}
      {comp.type === 'nav-button' && (
        <div className={sectionCls}>
          <label className={labelCls}>Jumps To</label>
          <select
            className={fieldCls}
            value={comp.navTargetLayoutId || ''}
            onChange={e => onChange({ navTargetLayoutId: e.target.value || undefined })}
          >
            <option value="">— Not set —</option>
            <option value="__back__">← Go Back (previous screen)</option>
            {otherScreens.map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
          <p className="text-[10px] text-slate-500 mt-1">
            Clicking this button in the live Viewer jumps straight to the chosen screen, or back to
            whichever screen was open before it. Add a matching "Go Back" button on the target screen
            so the operator can return.
          </p>
        </div>
      )}

      {/* Live data binding */}
      <div className={sectionCls}>
        <p className={labelCls}>Live Data Binding</p>
        {availableTags.length > 0 ? (
          <TagSearchSelect
            tags={availableTags}
            value={currentBinding}
            onSelect={(plcId, tagName) => onChange({ plcId, tagName })}
          />
        ) : (
          <>
            <label className={labelCls}>PLC ID</label>
            <input className={`${fieldCls} mb-1`} value={comp.plcId || ''}
              placeholder="PLC ID" onChange={e => onChange({ plcId: e.target.value })} />
            <label className={labelCls}>Tag Name</label>
            <input className={fieldCls} value={comp.tagName || ''}
              placeholder="e.g. PT-01" onChange={e => onChange({ tagName: e.target.value })} />
          </>
        )}
      </div>

      {/* Min / Max */}
      {!['valve', 'pump', 'motor', 'motor-industrial', 'agitator', 'label', 'line', 'image', 'arrow'].includes(comp.type) && (
        <div className={sectionCls}>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={labelCls}>Min</label>
              <input type="number" className={fieldCls} value={comp.minValue ?? 0}
                onChange={e => onChange({ minValue: Number(e.target.value) })} />
            </div>
            <div>
              <label className={labelCls}>Max</label>
              <input type="number" className={fieldCls} value={comp.maxValue ?? 100}
                onChange={e => onChange({ maxValue: Number(e.target.value) })} />
            </div>
          </div>
        </div>
      )}

      {/* ON threshold */}
      {['valve', 'pump', 'motor', 'motor-industrial', 'agitator', 'pipe-h', 'pipe-v', 'pipe-elbow-l', 'pipe-elbow-r'].includes(comp.type) && (
        <div className={sectionCls}>
          <label className={labelCls}>ON when value ≥</label>
          <input type="number" className={fieldCls} value={comp.onValue ?? 1}
            onChange={e => onChange({ onValue: Number(e.target.value) })} />
        </div>
      )}

      {/* Flow condition — pipes only: mirror another component's ON/OFF state instead
          of needing their own tag binding (e.g. "only flows when this valve is open") */}
      {['pipe-h', 'pipe-v', 'pipe-elbow-l', 'pipe-elbow-r'].includes(comp.type) && (
        <div className={sectionCls}>
          <label className={labelCls}>Flow Condition</label>
          <select className={fieldCls}
            value={comp.flowSourceId || ''}
            onChange={e => onChange({ flowSourceId: e.target.value || undefined })}
          >
            <option value="">Use its own tag binding</option>
            {components.filter(c => c.id !== comp.id).map(c => (
              <option key={c.id} value={c.id}>{c.label || c.type} ({c.type.replace('-', ' ')})</option>
            ))}
          </select>
          <p className="text-[10px] text-slate-500 mt-1">
            Pick a valve/pump/etc. so this pipe only shows flow while that component is ON — instead of binding this pipe to its own tag.
          </p>
        </div>
      )}

      {/* Flow direction — pipes only: which way the fluid animation travels through
          the pipe, independent of the pipe's own rotation/orientation. */}
      {['pipe-h', 'pipe-v', 'pipe-elbow-l', 'pipe-elbow-r'].includes(comp.type) && (
        <div className={sectionCls}>
          <label className={labelCls}>Flow Direction</label>
          <div className="grid grid-cols-2 gap-1.5">
            <button
              type="button"
              onClick={() => onChange({ flowReverse: false })}
              className={`py-1.5 rounded text-xs font-semibold border transition-colors ${
                !comp.flowReverse ? 'bg-blue-600 border-blue-500 text-white' : 'border-slate-700 text-slate-400 hover:bg-slate-800'
              }`}
            >
              Forward
            </button>
            <button
              type="button"
              onClick={() => onChange({ flowReverse: true })}
              className={`py-1.5 rounded text-xs font-semibold border transition-colors ${
                comp.flowReverse ? 'bg-blue-600 border-blue-500 text-white' : 'border-slate-700 text-slate-400 hover:bg-slate-800'
              }`}
            >
              Reverse
            </button>
          </div>
          <p className="text-[10px] text-slate-500 mt-1">
            Which way the oil/water/gas animation flows through this pipe — flip it without rotating the pipe itself.
          </p>
        </div>
      )}

      {/* Unit */}
      <div className={sectionCls}>
        <label className={labelCls}>Unit</label>
        <select className={fieldCls}
          value={unitIsCustom ? '__custom__' : (comp.unit || '__none__')}
          onChange={e => {
            const val = e.target.value;
            if (val === '__custom__') {
              setUnitIsCustom(true);
              onChange({ unit: '' });
            } else if (val === '__none__') {
              setUnitIsCustom(false);
              onChange({ unit: '' });
            } else {
              setUnitIsCustom(false);
              onChange({ unit: val });
            }
          }}
        >
          <option value="__none__">— None —</option>
          {COMMON_HMI_UNITS.map(group => (
            <optgroup key={group.group} label={group.group}>
              {group.units.map(u => <option key={u} value={u}>{u}</option>)}
            </optgroup>
          ))}
          <option value="__custom__">Custom…</option>
        </select>
        {unitIsCustom && (
          <input className={`${fieldCls} mt-1.5`} value={comp.unit || ''}
            onChange={e => onChange({ unit: e.target.value })}
            placeholder="Enter custom unit" autoFocus />
        )}
      </div>

      {/* Fluid type — pipes/tanks only */}
      {['pipe-h', 'pipe-v', 'pipe-elbow-l', 'pipe-elbow-r', 'tank', 'tank-h'].includes(comp.type) && (
        <div className={sectionCls}>
          <label className={labelCls}>Fluid Type</label>
          <select className={fieldCls}
            value={comp.fluidType || 'custom'}
            onChange={e => onChange({ fluidType: e.target.value })}
          >
            {FLUID_TYPES.map(f => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>
        </div>
      )}

      {/* Component (housing/shell) color — independent of the fluid/accent color below */}
      {!['label', 'line', 'image', 'arrow', 'nav-button'].includes(comp.type) && (
        <div className={sectionCls}>
          <label className={labelCls}>Component Color</label>
          <div className="flex items-center gap-2">
            <input type="color" className="w-8 h-7 rounded border border-slate-700 bg-transparent cursor-pointer"
              value={comp.shellColor || '#94a3b8'}
              onChange={e => onChange({ shellColor: e.target.value })} />
            <span className="text-xs text-slate-400 font-mono">{comp.shellColor || '#94a3b8'}</span>
          </div>
          <p className="text-[10px] text-slate-500 mt-1">
            Colors the physical housing/shell (tank body, pipe, valve casing, etc.) — separate from the
            fluid/status color below, so recoloring the tank doesn't recolor the water.
          </p>
        </div>
      )}

      {/* Color */}
      {comp.type !== 'image' && (
        <div className={sectionCls}>
          <label className={labelCls}>
            Accent Color
            {comp.fluidType && comp.fluidType !== 'custom' && (
              <span className="normal-case font-normal text-slate-500"> (overridden by fluid type)</span>
            )}
          </label>
          <div className="flex items-center gap-2">
            <input type="color" className="w-8 h-7 rounded border border-slate-700 bg-transparent cursor-pointer disabled:opacity-40"
              disabled={!!comp.fluidType && comp.fluidType !== 'custom'}
              value={comp.color || '#3b82f6'}
              onChange={e => onChange({ color: e.target.value })} />
            <span className="text-xs text-slate-400 font-mono">{comp.color || '#3b82f6'}</span>
          </div>
          <p className="text-[10px] text-slate-500 mt-1">
            {['line', 'arrow'].includes(comp.type)
              ? 'Sets the line/arrow color.'
              : comp.type === 'valve-fcv'
                ? 'Sets the "fully open" (100%) color — it still sweeps from red at 0% to this color at 100%.'
                : 'Drives the liquid/flow color and the open/running status glow.'}
          </p>
        </div>
      )}

      {/* Line thickness + style — line/arrow only */}
      {['line', 'arrow'].includes(comp.type) && (
        <div className={sectionCls}>
          <label className={labelCls}>{comp.type === 'arrow' ? 'Arrow Thickness' : 'Line Thickness'}</label>
          <div className="flex items-center gap-1.5">
            <input type="range" min={1} max={20} step={1}
              className="flex-1 accent-blue-500"
              value={comp.lineThickness ?? 3}
              onChange={e => onChange({ lineThickness: Number(e.target.value) })} />
            <span className="text-slate-300 w-6 text-right text-xs">{comp.lineThickness ?? 3}</span>
          </div>
          <label className={labelCls + ' mt-2.5'}>Line Style</label>
          <select className={fieldCls}
            value={comp.lineStyle || 'solid'}
            onChange={e => onChange({ lineStyle: e.target.value as 'solid' | 'dashed' | 'dotted' })}
          >
            <option value="solid">Solid</option>
            <option value="dashed">Dashed</option>
            <option value="dotted">Dotted</option>
          </select>
        </div>
      )}

      {/* Alert thresholds */}
      {!['label', 'line', 'image', 'arrow'].includes(comp.type) && (
        <div className={sectionCls}>
          <p className={labelCls}>Alert Thresholds</p>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={labelCls}>High</label>
              <input type="number" className={fieldCls}
                value={comp.alertHigh ?? ''} placeholder="—"
                onChange={e => onChange({ alertHigh: e.target.value ? Number(e.target.value) : undefined })} />
            </div>
            <div>
              <label className={labelCls}>Low</label>
              <input type="number" className={fieldCls}
                value={comp.alertLow ?? ''} placeholder="—"
                onChange={e => onChange({ alertLow: e.target.value ? Number(e.target.value) : undefined })} />
            </div>
          </div>
        </div>
      )}

      {/* Size */}
      <div className={sectionCls}>
        <p className={labelCls}>Size</p>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className={labelCls}>W</label>
            <input type="number" className={fieldCls} value={comp.width}
              onChange={e => onChange({ width: Number(e.target.value) })} />
          </div>
          <div>
            <label className={labelCls}>H</label>
            <input type="number" className={fieldCls} value={comp.height}
              onChange={e => onChange({ height: Number(e.target.value) })} />
          </div>
        </div>
      </div>

      {/* Font size — all types */}
      <div className={sectionCls}>
        <label className={labelCls}>Font Size (text/labels)</label>
        <div className="flex items-center gap-1.5">
          <input type="range" min={6} max={28} step={1}
            className="flex-1 accent-blue-500"
            value={comp.fontSize || (comp.type === 'label' ? 14 : 10)}
            onChange={e => onChange({ fontSize: Number(e.target.value) })} />
          <span className="text-slate-300 w-6 text-right text-xs">{comp.fontSize || (comp.type === 'label' ? 14 : 10)}</span>
        </div>
      </div>

      {/* Status text size — tank/valve/pump/motor readouts (RUNNING/STOPPED, OPEN/CLOSED, level %) */}
      {['tank', 'tank-h', 'valve', 'valve-fcv', 'pump', 'motor', 'motor-industrial', 'agitator'].includes(comp.type) && (
        <div className={sectionCls}>
          <label className={labelCls}>Status Text Size</label>
          <div className="flex items-center gap-1.5">
            <input type="range" min={6} max={24} step={1}
              className="flex-1 accent-blue-500"
              value={comp.statusFontSize ?? 9}
              onChange={e => onChange({ statusFontSize: Number(e.target.value) })} />
            <span className="text-slate-300 w-6 text-right text-xs">{comp.statusFontSize ?? 9}</span>
          </div>
          <p className="text-[10px] text-slate-500 mt-1">
            Size of the small status readout (RUNNING/STOPPED, OPEN/CLOSED, level %) — separate from the tag/name label above.
          </p>
          <p className="text-[10px] text-slate-500 mt-1">
            Click the status text itself on the canvas to drag it anywhere, or nudge it with the arrow keys — just like the label.
          </p>
          {(comp.statusDx || comp.statusDy) ? (
            <button
              type="button"
              onClick={() => onChange({ statusDx: 0, statusDy: 0 })}
              className="mt-1.5 text-[10px] text-blue-400 hover:text-blue-300 font-semibold"
            >
              Reset status position
            </button>
          ) : null}
        </div>
      )}

      {/* Position */}
      <div className={sectionCls}>
        <p className={labelCls}>Position</p>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className={labelCls}>X</label>
            <input type="number" className={fieldCls} value={comp.x}
              onChange={e => onChange({ x: Math.max(0, Number(e.target.value)) })} />
          </div>
          <div>
            <label className={labelCls}>Y</label>
            <input type="number" className={fieldCls} value={comp.y}
              onChange={e => onChange({ y: Math.max(0, Number(e.target.value)) })} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ===================== SIMULATION PANEL =====================

function SimulationPanel({
  components,
  simValues,
  onValueChange,
  onReset,
}: {
  components: HMIComponent[];
  simValues: Record<string, number>;
  onValueChange: (id: string, val: number) => void;
  onReset: () => void;
}) {
  const isPipeType = (t: HMIComponentType) => t === 'pipe-h' || t === 'pipe-v' || t === 'pipe-elbow-l' || t === 'pipe-elbow-r';
  const items = components.filter(c =>
    !['label', 'line', 'image', 'arrow'].includes(c.type) && !isPipeType(c.type) && !(c.type === 'tank' && c.hasLevelSensor === false)
  );
  const pipes = components.filter(c => isPipeType(c.type));

  return (
    <div className="p-3 space-y-3 text-white text-xs overflow-y-auto">
      <div className="bg-emerald-900/30 border border-emerald-700/40 rounded-lg p-2.5">
        <p className="font-bold text-emerald-400 text-xs">Simulation Active</p>
        <p className="text-[10px] text-emerald-600 mt-0.5">Adjust values below to preview animations and states.</p>
      </div>

      <button onClick={onReset} className="w-full text-[10px] text-slate-400 hover:text-white border border-slate-700 rounded py-1 hover:bg-slate-800 transition-colors">
        Reset all to 0
      </button>

      {items.length === 0 && (
        <p className="text-slate-600 text-center py-4 text-[10px]">Add components to the canvas to simulate them.</p>
      )}

      {items.map(comp => {
        const val = simValues[comp.id] ?? 0;
        const min = comp.minValue ?? 0;
        const max = comp.maxValue ?? 100;
        const isBinary = ['valve', 'pump', 'motor', 'motor-industrial', 'agitator'].includes(comp.type);
        const onVal = comp.onValue ?? 1;

        return (
          <div key={comp.id} className="border border-slate-800 rounded-lg p-2.5 space-y-1.5 bg-slate-900/50">
            <div className="flex items-center justify-between">
              <div>
                <span className="font-semibold text-slate-200">{comp.label || comp.type}</span>
                <span className="ml-1.5 text-[9px] text-slate-500 capitalize">{comp.type.replace('-', ' ')}</span>
              </div>
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                isBinary
                  ? val >= onVal ? 'bg-emerald-900/50 text-emerald-400' : 'bg-rose-900/40 text-rose-400'
                  : 'text-blue-300 bg-blue-900/30'
              }`}>
                {isBinary ? (val >= onVal ? 'ON' : 'OFF') : `${val.toFixed(1)}`}
                {!isBinary && comp.unit ? ` ${comp.unit}` : ''}
              </span>
            </div>

            {isBinary ? (
              <div className="flex gap-2">
                <button
                  onClick={() => onValueChange(comp.id, 0)}
                  className={`flex-1 py-1 rounded text-[10px] font-semibold border transition-colors ${
                    val < onVal ? 'bg-rose-700 border-rose-600 text-white' : 'border-slate-700 text-slate-500 hover:bg-slate-800'
                  }`}
                >OFF</button>
                <button
                  onClick={() => onValueChange(comp.id, onVal)}
                  className={`flex-1 py-1 rounded text-[10px] font-semibold border transition-colors ${
                    val >= onVal ? 'bg-emerald-700 border-emerald-600 text-white' : 'border-slate-700 text-slate-500 hover:bg-slate-800'
                  }`}
                >ON</button>
              </div>
            ) : (
              <div className="space-y-1">
                <input
                  type="range" min={min} max={max} step={(max - min) / 100}
                  value={val}
                  onChange={e => onValueChange(comp.id, Number(e.target.value))}
                  className="w-full accent-blue-500"
                />
                <div className="flex justify-between text-[9px] text-slate-600">
                  <span>{min}</span>
                  <input
                    type="number" min={min} max={max}
                    value={val}
                    onChange={e => onValueChange(comp.id, Number(e.target.value))}
                    className="w-16 text-center bg-slate-800 border border-slate-700 rounded px-1 py-0.5 text-xs text-white"
                  />
                  <span>{max}</span>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Pipes — just on/off */}
      {pipes.length > 0 && (
        <div className="border-t border-slate-800 pt-2">
          <p className="text-[9px] text-slate-500 uppercase tracking-wide mb-2">Pipes (Flow on/off)</p>
          {pipes.map(comp => {
            const val = simValues[comp.id] ?? 0;
            const onVal = comp.onValue ?? 1;
            return (
              <div key={comp.id} className="flex items-center justify-between py-1">
                <span className="text-slate-300">{comp.label || comp.type}</span>
                <div className="flex gap-1.5">
                  <button onClick={() => onValueChange(comp.id, 0)}
                    className={`px-2 py-0.5 rounded text-[9px] border ${val < onVal ? 'bg-rose-700 border-rose-600 text-white' : 'border-slate-700 text-slate-500'}`}>OFF</button>
                  <button onClick={() => onValueChange(comp.id, onVal)}
                    className={`px-2 py-0.5 rounded text-[9px] border ${val >= onVal ? 'bg-emerald-700 border-emerald-600 text-white' : 'border-slate-700 text-slate-500'}`}>ON</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ===================== MULTI-SELECT PANEL =====================

function MultiSelectPanel({
  count, onDelete, onDuplicate, onRotateCW, onRotateCCW, onColorChange, onShellColorChange, onSetSize, onScale, onClear,
}: {
  count: number;
  onDelete: () => void;
  onDuplicate: () => void;
  onRotateCW: () => void;
  onRotateCCW: () => void;
  onColorChange: (color: string) => void;
  onShellColorChange: (color: string) => void;
  onSetSize: (width?: number, height?: number) => void;
  onScale: (factor: number) => void;
  onClear: () => void;
}) {
  const labelCls = "text-[10px] text-slate-400 block mb-1 font-semibold uppercase tracking-wide";
  const fieldCls = "w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-white focus:border-blue-500 outline-none";
  const sectionCls = "border-t border-slate-800 pt-3";

  const [bulkWidth, setBulkWidth]   = useState('');
  const [bulkHeight, setBulkHeight] = useState('');

  return (
    <div className="p-3 space-y-3 text-white text-xs">
      <div className="bg-slate-800 rounded-lg p-2">
        <p className="text-[10px] text-slate-500 uppercase tracking-wider">Selected</p>
        <p className="text-sm font-bold text-blue-400 mt-0.5">{count} components</p>
      </div>

      <div className={sectionCls}>
        <p className={labelCls}>Actions</p>
        <div className="grid grid-cols-2 gap-1">
          <IconBtn onClick={onDuplicate} title="Duplicate all (Ctrl+D)"><Copy className="w-3 h-3" />Duplicate</IconBtn>
          <IconBtn onClick={onDelete} title="Delete all (Del)" danger><Trash2 className="w-3 h-3" />Delete</IconBtn>
        </div>
      </div>

      <div className={sectionCls}>
        <p className={labelCls}>Resize All</p>
        <div className="grid grid-cols-2 gap-1.5 mb-1.5">
          <div>
            <label className="text-[9px] text-slate-500 block mb-0.5">Width</label>
            <input type="number" className={fieldCls} placeholder="px" value={bulkWidth}
              onChange={e => setBulkWidth(e.target.value)} />
          </div>
          <div>
            <label className="text-[9px] text-slate-500 block mb-0.5">Height</label>
            <input type="number" className={fieldCls} placeholder="px" value={bulkHeight}
              onChange={e => setBulkHeight(e.target.value)} />
          </div>
        </div>
        <button
          onClick={() => {
            const w = bulkWidth ? Number(bulkWidth) : undefined;
            const h = bulkHeight ? Number(bulkHeight) : undefined;
            onSetSize(w && w > 0 ? w : undefined, h && h > 0 ? h : undefined);
          }}
          disabled={!bulkWidth && !bulkHeight}
          className="w-full text-[10px] font-semibold text-white bg-blue-600 hover:bg-blue-500 disabled:bg-slate-800 disabled:text-slate-600 rounded py-1.5 transition-colors mb-2"
        >
          Apply size to all
        </button>
        <p className="text-[10px] text-slate-500 -mt-1 mb-2">Fill in just Width or just Height to change only that dimension.</p>
        <label className="text-[9px] text-slate-500 block mb-1">Or scale every selected item proportionally</label>
        <div className="grid grid-cols-4 gap-1">
          <IconBtn onClick={() => onScale(0.9)} title="Shrink 10%">-10%</IconBtn>
          <IconBtn onClick={() => onScale(1.1)} title="Grow 10%">+10%</IconBtn>
          <IconBtn onClick={() => onScale(0.5)} title="Shrink 50%">50%</IconBtn>
          <IconBtn onClick={() => onScale(2)} title="Double size">200%</IconBtn>
        </div>
      </div>

      <div className={sectionCls}>
        <p className={labelCls}>Rotate All</p>
        <div className="flex items-center gap-1.5">
          <IconBtn onClick={onRotateCCW} title="Rotate 45° CCW"><RotateCcw className="w-3 h-3" />-45°</IconBtn>
          <IconBtn onClick={onRotateCW} title="Rotate 45° CW"><RotateCw className="w-3 h-3" />+45°</IconBtn>
        </div>
      </div>

      <div className={sectionCls}>
        <label className={labelCls}>Set Component Color (all)</label>
        <input
          type="color"
          className="w-full h-8 rounded border border-slate-700 bg-transparent cursor-pointer"
          defaultValue="#94a3b8"
          onChange={e => onShellColorChange(e.target.value)}
        />
        <p className="text-[10px] text-slate-500 mt-1">Recolors the housing/shell of every selected component.</p>
      </div>

      <div className={sectionCls}>
        <label className={labelCls}>Set Accent Color (all)</label>
        <input
          type="color"
          className="w-full h-8 rounded border border-slate-700 bg-transparent cursor-pointer"
          defaultValue="#3b82f6"
          onChange={e => onColorChange(e.target.value)}
        />
        <p className="text-[10px] text-slate-500 mt-1">Sets the liquid/flow/status color of every selected component.</p>
      </div>

      <button
        onClick={onClear}
        className="w-full text-[10px] text-slate-400 hover:text-white border border-slate-700 rounded py-1.5 hover:bg-slate-800 transition-colors"
      >
        Clear selection
      </button>
    </div>
  );
}

// ===================== HMI BUILDER =====================

interface HMIBuilderProps {
  token: string;
}

export function HMIBuilder({ token }: HMIBuilderProps) {
  const [clients, setClients]             = useState<{ id: string; name: string }[]>([]);
  const [clientId, setClientId]           = useState('');
  const [layouts, setLayouts]             = useState<SavedHMILayout[]>([]);
  const [currentId, setCurrentId]         = useState<string | null>(null);
  const [layoutName, setLayoutName]       = useState('New Layout');
  const [background, setBackground]       = useState(DEFAULT_LAYOUT.background);
  const [components, setComponents]       = useState<HMIComponent[]>([]);
  const [selectedIds, setSelectedIds]     = useState<string[]>([]);
  const [saving, setSaving]               = useState(false);
  const [availableTags, setAvailableTags] = useState<{ plcId: string; plcName: string; tagName: string }[]>([]);
  const [isSimulating, setIsSimulating]   = useState(false);
  const [simValues, setSimValues]         = useState<Record<string, number>>({});
  const [marqueeRect, setMarqueeRect]     = useState<{ x: number; y: number; w: number; h: number } | null>(null);

  // ---- History for undo/redo ----
  const historyRef    = useRef<HMIComponent[][]>([[]]);
  const historyIdxRef = useRef(0);
  const isDraggingRef = useRef(false);
  const marqueeRef     = useRef<{ x0: number; y0: number } | null>(null);

  const pushHistory = useCallback((comps: HMIComponent[]) => {
    const h = historyRef.current.slice(0, historyIdxRef.current + 1);
    h.push(comps.map(c => ({ ...c }))); // deep clone
    historyRef.current   = h;
    historyIdxRef.current = h.length - 1;
  }, []);

  const setAndPush = useCallback((updater: (prev: HMIComponent[]) => HMIComponent[]) => {
    setComponents(prev => {
      const next = updater(prev);
      pushHistory(next);
      return next;
    });
  }, [pushHistory]);

  const undo = useCallback(() => {
    if (historyIdxRef.current <= 0) return;
    historyIdxRef.current--;
    setComponents([...historyRef.current[historyIdxRef.current]]);
    setSelectedIds([]);
  }, []);

  const redo = useCallback(() => {
    if (historyIdxRef.current >= historyRef.current.length - 1) return;
    historyIdxRef.current++;
    setComponents([...historyRef.current[historyIdxRef.current]]);
    setSelectedIds([]);
  }, []);

  const svgRef  = useRef<SVGSVGElement>(null);
  const dragRef = useRef<{ ids: string[]; sx: number; sy: number; origins: Record<string, { x: number; y: number }> } | null>(null);

  // ---- Label drag/nudge (a widget's tag name can be repositioned independently) ----
  const [labelTargetId, setLabelTargetId] = useState<string | null>(null);
  const labelDragRef = useRef<{ id: string; sx: number; sy: number; originDx: number; originDy: number } | null>(null);

  // ---- Status-readout drag/nudge (RUNNING/STOPPED, OPEN/CLOSED, level %, value)
  // — same idea as the label drag above, just for the status text instead. ----
  const [statusTargetId, setStatusTargetId] = useState<string | null>(null);
  const statusDragRef = useRef<{ id: string; sx: number; sy: number; originDx: number; originDy: number } | null>(null);

  // ---- Line/Arrow endpoint drag — grab either end directly instead of fighting
  // separate Width + Rotation fields after rotating. `fixed` is the OTHER endpoint's
  // canvas position, captured once at drag-start, which stays put while the dragged
  // end follows the mouse; x/y/width/rotation are recomputed from those two points
  // every move so the shape always matches exactly where the user is pointing. ----
  const endpointDragRef = useRef<{ id: string; which: 'start' | 'end'; fixed: { x: number; y: number }; height: number } | null>(null);

  const selectedComp = selectedIds.length === 1 ? (components.find(c => c.id === selectedIds[0]) ?? null) : null;

  const getToken = () => authSession.get()?.token || token || '';

  // A stale/expired token (default 24h TTL — see AUTH_TOKEN_TTL_SECONDS) makes every
  // authenticated request 401 while the app still looks logged in. Instead of each
  // call site surfacing the raw `{"error":"..."}` body or silently failing, funnel
  // every non-OK response through here: friendly message, and a forced re-login on 401.
  const sessionExpiredRef = useRef(false);
  const handleSessionExpired = useCallback(() => {
    if (sessionExpiredRef.current) return; // only announce/redirect once per page
    sessionExpiredRef.current = true;
    toast.error('Your session has expired. Logging you out…');
    authSession.clear();
    setTimeout(() => window.location.reload(), 1200);
  }, []);

  const readErrorMessage = async (r: Response): Promise<string> => {
    let raw = '';
    try { raw = await r.text(); } catch { /* body already consumed / unreadable */ }
    if (raw) {
      try {
        const data = JSON.parse(raw);
        if (data?.error) return String(data.error);
      } catch { /* not JSON — fall through to raw text */ }
    }
    return raw || `Request failed (${r.status})`;
  };

  // Checks a fetch response for auth/HTTP failures. Returns true and shows a toast
  // (or triggers logout, for 401) if the request failed; false means the caller
  // can proceed to read the body.
  const handleFailedResponse = useCallback(async (r: Response, fallback: string) => {
    if (r.ok) return false;
    if (r.status === 401) { handleSessionExpired(); return true; }
    toast.error(await readErrorMessage(r) || fallback);
    return true;
  }, [handleSessionExpired]);

  // Load client list
  useEffect(() => {
    const t = getToken();
    if (!t) return;
    fetch(`${API_BASE}/api/clients`, { headers: { Authorization: `Bearer ${t}` } })
      .then(async r => {
        if (await handleFailedResponse(r, 'Failed to load clients')) return null;
        return r.json();
      })
      .then(d => { if (d) setClients(Array.isArray(d) ? d : d.clients || []); })
      .catch(() => {});
  }, []);

  // Load layouts + available tags when client changes
  const loadLayouts = useCallback(() => {
    if (!clientId) return;
    const t = getToken();
    if (!t) return;
    const headers = { Authorization: `Bearer ${t}` };
    fetch(`${API_BASE}/api/admin/clients/${clientId}/hmi-layouts`, { headers })
      .then(async r => {
        if (await handleFailedResponse(r, 'Failed to load layouts')) return null;
        return r.json();
      })
      .then(d => { if (d) setLayouts(d.layouts || []); })
      .catch(() => {});
    fetch(`${API_BASE}/api/admin/clients/${clientId}/plcs-for-hmi`, { headers })
      .then(async r => {
        if (await handleFailedResponse(r, 'Failed to load tags')) return null;
        return r.json();
      })
      .then(d => { if (d) setAvailableTags(d.tags || []); })
      .catch(() => {});
  }, [clientId, handleFailedResponse]);

  useEffect(() => { loadLayouts(); }, [loadLayouts]);

  // ---- Canvas coords ----
  const toSVG = (clientX: number, clientY: number) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const rect = svg.getBoundingClientRect();
    return {
      x: ((clientX - rect.left) / rect.width) * CANVAS_W,
      y: ((clientY - rect.top) / rect.height) * CANVAS_H,
    };
  };

  // ---- Add component from palette ----
  const addComponent = (type: HMIComponentType) => {
    const def  = COMPONENT_DEFAULTS[type];
    const item = PALETTE_ITEMS.find(p => p.type === type)!;
    const newComp: HMIComponent = {
      id:       genId(),
      type,
      x:        snap(CANVAS_W / 2 - def.width / 2),
      y:        snap(CANVAS_H / 2 - def.height / 2),
      ...def,
      label:    item.defaultLabel,
      color:    '#3b82f6',
      minValue: 0,
      maxValue: 100,
      onValue:  1,
      unit:     '',
      rotation: 0,
    };
    setAndPush(prev => [...prev, newComp]);
    setSelectedIds([newComp.id]);
  };

  // ---- Duplicate selected (all of them) ----
  const duplicateSelected = useCallback(() => {
    if (selectedIds.length === 0) return;
    const idSet = new Set(selectedIds);
    const copies = components
      .filter(c => idSet.has(c.id))
      .map(c => ({ ...c, id: genId(), x: c.x + GRID * 2, y: c.y + GRID * 2 }));
    if (copies.length === 0) return;
    setAndPush(prev => [...prev, ...copies]);
    setSelectedIds(copies.map(c => c.id));
  }, [selectedIds, components, setAndPush]);

  // ---- Copy / Paste (clipboard survives switching to a different page/screen) ----
  const clipboardRef = useRef<HMIComponent[]>([]);
  const [hasClipboard, setHasClipboard] = useState(false);

  const copySelected = useCallback(() => {
    if (selectedIds.length === 0) return;
    const idSet = new Set(selectedIds);
    clipboardRef.current = components.filter(c => idSet.has(c.id)).map(c => ({ ...c }));
    setHasClipboard(clipboardRef.current.length > 0);
    toast.success(`Copied ${clipboardRef.current.length} component${clipboardRef.current.length === 1 ? '' : 's'}`);
  }, [selectedIds, components]);

  const pasteClipboard = useCallback(() => {
    if (clipboardRef.current.length === 0) return;
    const copies = clipboardRef.current.map(c => ({ ...c, id: genId() }));
    setAndPush(prev => [...prev, ...copies]);
    setSelectedIds(copies.map(c => c.id));
    toast.success(`Pasted ${copies.length} component${copies.length === 1 ? '' : 's'}`);
  }, [setAndPush]);

  // ---- Label drag (click the tag/name text itself to move just the label) ----
  const handleLabelMouseDown = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setSelectedIds([id]);
    setLabelTargetId(id);
    setStatusTargetId(null);
    const { x, y } = toSVG(e.clientX, e.clientY);
    const c = components.find(cc => cc.id === id);
    labelDragRef.current = { id, sx: x, sy: y, originDx: c?.labelDx ?? 0, originDy: c?.labelDy ?? 0 };
    dragRef.current = null;
    isDraggingRef.current = false;
  };

  // ---- Status-readout drag (click the RUNNING/STOPPED/OPEN/CLOSED/level% text
  // itself to move just that readout, independent of the widget body) ----
  const handleStatusMouseDown = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setSelectedIds([id]);
    setStatusTargetId(id);
    setLabelTargetId(null);
    const { x, y } = toSVG(e.clientX, e.clientY);
    const c = components.find(cc => cc.id === id);
    statusDragRef.current = { id, sx: x, sy: y, originDx: c?.statusDx ?? 0, originDy: c?.statusDy ?? 0 };
    dragRef.current = null;
    isDraggingRef.current = false;
  };

  // ---- Endpoint drag (Line/Arrow only) — grab either end and reposition it directly ----
  const handleEndpointMouseDown = (e: React.MouseEvent, id: string, which: 'start' | 'end') => {
    e.stopPropagation();
    setSelectedIds([id]);
    setLabelTargetId(null);
    setStatusTargetId(null);
    const c = components.find(cc => cc.id === id);
    if (!c) return;
    const { start, end } = lineEndpoints(c);
    endpointDragRef.current = { id, which, fixed: which === 'start' ? end : start, height: c.height };
    dragRef.current = null;
    isDraggingRef.current = false;
  };

  // ---- Drag handlers ----
  const handleCompMouseDown = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setLabelTargetId(null);
    setStatusTargetId(null);
    const additive = e.shiftKey || e.metaKey || e.ctrlKey;
    const wasSelected = selectedIds.includes(id);
    let nextSelected: string[];

    if (additive) {
      nextSelected = wasSelected ? selectedIds.filter(sid => sid !== id) : [...selectedIds, id];
    } else if (wasSelected && selectedIds.length > 1) {
      nextSelected = selectedIds; // clicking within an existing multi-selection drags the whole group
    } else {
      nextSelected = [id];
    }
    setSelectedIds(nextSelected);

    // Shift-click to deselect shouldn't also start a drag.
    if (additive && wasSelected) { dragRef.current = null; return; }

    const { x, y } = toSVG(e.clientX, e.clientY);
    const origins: Record<string, { x: number; y: number }> = {};
    nextSelected.forEach(sid => {
      const c = components.find(cc => cc.id === sid);
      if (c) origins[sid] = { x: c.x, y: c.y };
    });
    dragRef.current = { ids: nextSelected, sx: x, sy: y, origins };
    isDraggingRef.current = false;
  };

  // ---- Marquee (drag-rectangle) select on empty canvas ----
  const handleSVGMouseDown = (e: React.MouseEvent) => {
    const t = e.target as SVGElement;
    const isEmpty = t === svgRef.current || t.getAttribute('fill') === 'url(#hmi-grid-b)';
    if (!isEmpty) return;
    setLabelTargetId(null);
    setStatusTargetId(null);
    const { x, y } = toSVG(e.clientX, e.clientY);
    marqueeRef.current = { x0: x, y0: y };
    setMarqueeRect({ x, y, w: 0, h: 0 });
  };

  const handleSVGMouseMove = (e: React.MouseEvent) => {
    if (labelDragRef.current) {
      const { x, y } = toSVG(e.clientX, e.clientY);
      const { id, sx, sy, originDx, originDy } = labelDragRef.current;
      const dx = x - sx;
      const dy = y - sy;
      setComponents(prev => prev.map(c => c.id === id ? { ...c, labelDx: originDx + dx, labelDy: originDy + dy } : c));
      return;
    }
    if (statusDragRef.current) {
      const { x, y } = toSVG(e.clientX, e.clientY);
      const { id, sx, sy, originDx, originDy } = statusDragRef.current;
      const dx = x - sx;
      const dy = y - sy;
      setComponents(prev => prev.map(c => c.id === id ? { ...c, statusDx: originDx + dx, statusDy: originDy + dy } : c));
      return;
    }
    if (endpointDragRef.current) {
      const { id, which, fixed, height } = endpointDragRef.current;
      const { x: mx, y: my } = toSVG(e.clientX, e.clientY);
      const dragged = { x: snap(mx), y: snap(my) };
      const p0 = which === 'start' ? dragged : fixed; // local x=0 end
      const p1 = which === 'start' ? fixed : dragged; // local x=width end
      const width = Math.max(GRID, Math.hypot(p1.x - p0.x, p1.y - p0.y));
      const rotation = (Math.atan2(p1.y - p0.y, p1.x - p0.x) * 180) / Math.PI;
      const cx = (p0.x + p1.x) / 2;
      const cy = (p0.y + p1.y) / 2;
      setComponents(prev => prev.map(c => c.id === id
        ? { ...c, x: cx - width / 2, y: cy - height / 2, width, rotation: ((rotation % 360) + 360) % 360 }
        : c
      ));
      return;
    }
    if (dragRef.current) {
      isDraggingRef.current = true;
      const { x, y } = toSVG(e.clientX, e.clientY);
      const dx = x - dragRef.current.sx;
      const dy = y - dragRef.current.sy;
      const { origins } = dragRef.current;
      setComponents(prev => prev.map(c => {
        const origin = origins[c.id];
        if (!origin) return c;
        return { ...c, x: Math.max(0, snap(origin.x + dx)), y: Math.max(0, snap(origin.y + dy)) };
      }));
      return;
    }
    if (marqueeRef.current) {
      const { x, y } = toSVG(e.clientX, e.clientY);
      setMarqueeRect({
        x: Math.min(marqueeRef.current.x0, x),
        y: Math.min(marqueeRef.current.y0, y),
        w: Math.abs(x - marqueeRef.current.x0),
        h: Math.abs(y - marqueeRef.current.y0),
      });
    }
  };

  const stopDrag = () => {
    if (labelDragRef.current) {
      setComponents(prev => { pushHistory(prev); return prev; });
      labelDragRef.current = null;
      return;
    }
    if (statusDragRef.current) {
      setComponents(prev => { pushHistory(prev); return prev; });
      statusDragRef.current = null;
      return;
    }
    if (endpointDragRef.current) {
      setComponents(prev => { pushHistory(prev); return prev; });
      endpointDragRef.current = null;
      return;
    }
    if (dragRef.current) {
      if (isDraggingRef.current) {
        setComponents(prev => { pushHistory(prev); return prev; });
      }
      dragRef.current = null;
      isDraggingRef.current = false;
      return;
    }
    if (marqueeRef.current) {
      const rect = marqueeRect;
      marqueeRef.current = null;
      setMarqueeRect(null);
      const wasRealDrag = !!rect && (rect.w > 3 || rect.h > 3);
      if (wasRealDrag && rect) {
        const hits = components
          .filter(c => c.x < rect.x + rect.w && c.x + c.width > rect.x && c.y < rect.y + rect.h && c.y + c.height > rect.y)
          .map(c => c.id);
        setSelectedIds(hits);
      } else {
        setSelectedIds([]);
      }
    }
  };

  // ---- Prop update (single-select only — the panel that calls this is only shown for one) ----
  const updateProp = useCallback((patch: Partial<HMIComponent>) => {
    if (selectedIds.length !== 1) return;
    const id = selectedIds[0];
    setAndPush(prev => prev.map(c => c.id === id ? { ...c, ...patch } : c));
  }, [selectedIds, setAndPush]);

  // ---- Z-order (single-select only) ----
  const bringToFront = useCallback(() => {
    if (selectedIds.length !== 1) return;
    const id = selectedIds[0];
    setAndPush(prev => {
      const idx = prev.findIndex(c => c.id === id);
      if (idx < 0 || idx === prev.length - 1) return prev;
      const next = [...prev];
      next.push(next.splice(idx, 1)[0]);
      return next;
    });
  }, [selectedIds, setAndPush]);

  const sendToBack = useCallback(() => {
    if (selectedIds.length !== 1) return;
    const id = selectedIds[0];
    setAndPush(prev => {
      const idx = prev.findIndex(c => c.id === id);
      if (idx <= 0) return prev;
      const next = [...prev];
      next.unshift(next.splice(idx, 1)[0]);
      return next;
    });
  }, [selectedIds, setAndPush]);

  const bringForward = useCallback(() => {
    if (selectedIds.length !== 1) return;
    const id = selectedIds[0];
    setAndPush(prev => {
      const idx = prev.findIndex(c => c.id === id);
      if (idx < 0 || idx === prev.length - 1) return prev;
      const next = [...prev];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return next;
    });
  }, [selectedIds, setAndPush]);

  const sendBackward = useCallback(() => {
    if (selectedIds.length !== 1) return;
    const id = selectedIds[0];
    setAndPush(prev => {
      const idx = prev.findIndex(c => c.id === id);
      if (idx <= 0) return prev;
      const next = [...prev];
      [next[idx], next[idx - 1]] = [next[idx - 1], next[idx]];
      return next;
    });
  }, [selectedIds, setAndPush]);

  // ---- Rotation (applies to every selected component) ----
  const rotateCW = useCallback(() => {
    if (selectedIds.length === 0) return;
    const idSet = new Set(selectedIds);
    setAndPush(prev => prev.map(c => idSet.has(c.id) ? { ...c, rotation: ((c.rotation ?? 0) + 45) % 360 } : c));
  }, [selectedIds, setAndPush]);

  const rotateCCW = useCallback(() => {
    if (selectedIds.length === 0) return;
    const idSet = new Set(selectedIds);
    setAndPush(prev => prev.map(c => idSet.has(c.id) ? { ...c, rotation: ((c.rotation ?? 0) - 45 + 360) % 360 } : c));
  }, [selectedIds, setAndPush]);

  // ---- Bulk color (applies to every selected component) ----
  const bulkSetColor = useCallback((color: string) => {
    if (selectedIds.length === 0) return;
    const idSet = new Set(selectedIds);
    setAndPush(prev => prev.map(c => idSet.has(c.id) ? { ...c, color } : c));
  }, [selectedIds, setAndPush]);

  const bulkSetShellColor = useCallback((shellColor: string) => {
    if (selectedIds.length === 0) return;
    const idSet = new Set(selectedIds);
    setAndPush(prev => prev.map(c => idSet.has(c.id) ? { ...c, shellColor } : c));
  }, [selectedIds, setAndPush]);

  // ---- Bulk resize (applies to every selected component). Either dimension can be
  // omitted — e.g. setting only width leaves each component's own height untouched. ----
  const bulkSetSize = useCallback((width?: number, height?: number) => {
    if (selectedIds.length === 0) return;
    const idSet = new Set(selectedIds);
    setAndPush(prev => prev.map(c => idSet.has(c.id)
      ? { ...c, width: width ?? c.width, height: height ?? c.height }
      : c
    ));
  }, [selectedIds, setAndPush]);

  const bulkScale = useCallback((factor: number) => {
    if (selectedIds.length === 0) return;
    const idSet = new Set(selectedIds);
    setAndPush(prev => prev.map(c => idSet.has(c.id)
      ? { ...c, width: Math.max(4, Math.round(c.width * factor)), height: Math.max(4, Math.round(c.height * factor)) }
      : c
    ));
  }, [selectedIds, setAndPush]);

  // ---- Layout management ----
  const newLayout = () => {
    setCurrentId(null);
    setLayoutName('New Layout');
    setBackground(DEFAULT_LAYOUT.background);
    setComponents([]);
    setSelectedIds([]);
    historyRef.current    = [[]];
    historyIdxRef.current = 0;
  };

  const loadLayout = (saved: SavedHMILayout) => {
    setCurrentId(saved.id);
    setLayoutName(saved.name);
    setBackground(saved.layout.background || DEFAULT_LAYOUT.background);
    const comps = saved.layout.components || [];
    setComponents(comps);
    setSelectedIds([]);
    historyRef.current    = [comps];
    historyIdxRef.current = 0;
  };

  // ---- Prev / Next page navigation (same order as the page tabs) ----
  const currentPageIndex = layouts.findIndex(l => l.id === currentId);
  const goToPage = (delta: number) => {
    if (layouts.length === 0) return;
    const baseIdx = currentPageIndex === -1 ? 0 : currentPageIndex;
    const nextIdx = Math.max(0, Math.min(layouts.length - 1, baseIdx + delta));
    const target = layouts[nextIdx];
    if (target && target.id !== currentId) loadLayout(target);
  };

  // ---- Reorder pages (which screen the Client Portal viewer opens 1st, and the
  // order Nav Button "go to screen" pickers list them in) — swaps the current page
  // with its neighbor and persists the new order for the whole client at once. ----
  const reorderPage = async (delta: -1 | 1) => {
    if (!clientId || currentPageIndex === -1) return;
    const targetIdx = currentPageIndex + delta;
    if (targetIdx < 0 || targetIdx >= layouts.length) return;
    const reordered = [...layouts];
    [reordered[currentPageIndex], reordered[targetIdx]] = [reordered[targetIdx], reordered[currentPageIndex]];
    setLayouts(reordered); // optimistic — reflects the swap immediately in the page tabs
    try {
      const headers = { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' };
      const r = await fetch(`${API_BASE}/api/admin/clients/${clientId}/hmi-layouts/reorder`, {
        method: 'PUT', headers, body: JSON.stringify({ order: reordered.map(l => l.id) }),
      });
      if (await handleFailedResponse(r, 'Reorder failed')) { loadLayouts(); return; }
    } catch {
      toast.error('Reorder failed — check your connection and try again');
      loadLayouts(); // roll back the optimistic swap
    }
  };

  const deleteLayout = async () => {
    if (!currentId) return;
    if (!confirm('Delete this layout?')) return;
    try {
      const headers = { Authorization: `Bearer ${getToken()}` };
      const r = await fetch(`${API_BASE}/api/admin/hmi-layouts/${currentId}`, { method: 'DELETE', headers });
      if (await handleFailedResponse(r, 'Delete failed')) return;
      toast.success('Layout deleted');
      newLayout();
      loadLayouts();
    } catch {
      toast.error('Delete failed — check your connection and try again');
    }
  };

  const saveLayout = async () => {
    if (!clientId)          { toast.error('Select a client first'); return; }
    if (!layoutName.trim()) { toast.error('Enter a layout name');   return; }
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' };
      const layout: HMILayout = { ...DEFAULT_LAYOUT, components, background };
      const body = JSON.stringify({ name: layoutName, layout });
      if (currentId) {
        const r = await fetch(`${API_BASE}/api/admin/hmi-layouts/${currentId}`, { method: 'PUT', headers, body });
        if (await handleFailedResponse(r, 'Save failed')) return;
        toast.success('Saved');
      } else {
        const r = await fetch(`${API_BASE}/api/admin/clients/${clientId}/hmi-layouts`, { method: 'POST', headers, body });
        if (await handleFailedResponse(r, 'Save failed')) return;
        const d = await r.json();
        setCurrentId(d.id);
        toast.success('Layout created');
      }
      loadLayouts();
    } catch (e: any) {
      toast.error(e.message || 'Save failed — check your connection and try again');
    } finally {
      setSaving(false);
    }
  };

  // ---- Delete selected components ----
  const deleteSelected = useCallback(() => {
    if (selectedIds.length === 0) return;
    const idSet = new Set(selectedIds);
    setAndPush(prev => prev.filter(c => !idSet.has(c.id)));
    setSelectedIds([]);
    setLabelTargetId(null);
    setStatusTargetId(null);
  }, [selectedIds, setAndPush]);

  // ---- Keyboard shortcuts ----
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      const inInput = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
      if (inInput) return;

      if (e.key === 'Delete' || e.key === 'Backspace') deleteSelected();
      if ((e.key === 'd' || e.key === 'D') && (e.ctrlKey || e.metaKey)) { e.preventDefault(); duplicateSelected(); }
      if ((e.key === 'c' || e.key === 'C') && (e.ctrlKey || e.metaKey) && selectedIds.length > 0) { e.preventDefault(); copySelected(); }
      if ((e.key === 'v' || e.key === 'V') && (e.ctrlKey || e.metaKey)) { e.preventDefault(); pasteClipboard(); }
      if ((e.key === 'z' || e.key === 'Z') && (e.ctrlKey || e.metaKey) && !e.shiftKey) { e.preventDefault(); undo(); }
      if (((e.key === 'y' || e.key === 'Y') && (e.ctrlKey || e.metaKey)) ||
          ((e.key === 'z' || e.key === 'Z') && (e.ctrlKey || e.metaKey) && e.shiftKey)) {
        e.preventDefault(); redo();
      }
      if (e.key === 'Escape' && (labelTargetId || statusTargetId)) { setLabelTargetId(null); setStatusTargetId(null); }

      // Arrow nudge — moves the active label/status readout if one is targeted, else every selected component
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key) && (labelTargetId || statusTargetId || selectedIds.length > 0)) {
        e.preventDefault();
        const step = e.shiftKey ? 10 : 1;
        const dx = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0;
        const dy = e.key === 'ArrowUp'   ? -step : e.key === 'ArrowDown'  ? step : 0;

        if (labelTargetId) {
          setAndPush(prev => prev.map(c => c.id === labelTargetId
            ? { ...c, labelDx: (c.labelDx ?? 0) + dx, labelDy: (c.labelDy ?? 0) + dy }
            : c
          ));
          return;
        }

        if (statusTargetId) {
          setAndPush(prev => prev.map(c => c.id === statusTargetId
            ? { ...c, statusDx: (c.statusDx ?? 0) + dx, statusDy: (c.statusDy ?? 0) + dy }
            : c
          ));
          return;
        }

        const idSet = new Set(selectedIds);
        setAndPush(prev => prev.map(c => idSet.has(c.id)
          ? { ...c, x: Math.max(0, c.x + dx), y: Math.max(0, c.y + dy) }
          : c
        ));
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [deleteSelected, duplicateSelected, copySelected, pasteClipboard, undo, redo, selectedIds, labelTargetId, statusTargetId, setAndPush]);

  const canUndo = historyIdxRef.current > 0;
  const canRedo = historyIdxRef.current < historyRef.current.length - 1;

  return (
    <div
      className="flex flex-col bg-slate-950 text-white -mx-4 -my-4 sm:-mx-6 sm:-my-6 lg:-mx-8 lg:-my-8"
      style={{ height: 'calc(100vh - 6rem)' }}
    >

      {/* ── Top toolbar ── */}
      <div className="flex items-center gap-1.5 px-3 py-2 border-b border-slate-800 bg-slate-900 flex-wrap shrink-0">

        {/* Brand */}
        <span className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mr-2">HMI Builder</span>

        {/* Client select */}
        <select
          className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-white"
          value={clientId}
          onChange={e => { setClientId(e.target.value); newLayout(); }}
        >
          <option value="">Select Client…</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>

        {clientId && (
          <input
            className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-white w-32"
            placeholder="Page name"
            value={layoutName}
            onChange={e => setLayoutName(e.target.value)}
          />
        )}

        {clientId && (
          <label
            title="Screen background color"
            className="flex items-center gap-1.5 bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-slate-400 cursor-pointer"
          >
            <input
              type="color"
              className="w-4 h-4 rounded border-none bg-transparent cursor-pointer p-0"
              value={background}
              onChange={e => setBackground(e.target.value)}
            />
            Background
          </label>
        )}

        {/* Divider */}
        <div className="w-px h-5 bg-slate-700 mx-1" />

        {/* Undo / Redo */}
        <IconBtn onClick={undo} title="Undo (Ctrl+Z)" disabled={!canUndo}>
          <Undo2 className="w-3.5 h-3.5" />
        </IconBtn>
        <IconBtn onClick={redo} title="Redo (Ctrl+Y)" disabled={!canRedo}>
          <Redo2 className="w-3.5 h-3.5" />
        </IconBtn>

        {/* Divider */}
        <div className="w-px h-5 bg-slate-700 mx-1" />

        {/* Copy / Paste — clipboard survives switching to a different page/screen */}
        <IconBtn onClick={copySelected} title="Copy (Ctrl+C)" disabled={selectedIds.length === 0}>
          <Copy className="w-3.5 h-3.5" />
        </IconBtn>
        <IconBtn onClick={pasteClipboard} title="Paste onto this page (Ctrl+V)" disabled={!hasClipboard}>
          <ClipboardPaste className="w-3.5 h-3.5" />
        </IconBtn>

        {/* Divider */}
        <div className="w-px h-5 bg-slate-700 mx-1" />

        {/* Save */}
        <button
          onClick={saveLayout}
          disabled={saving || !clientId}
          className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white px-3 py-1.5 rounded text-xs font-semibold"
        >
          <Save className="w-3.5 h-3.5" /> {saving ? 'Saving…' : 'Save'}
        </button>

        {/* Delete page */}
        {currentId && (
          <IconBtn onClick={deleteLayout} danger>
            <Trash2 className="w-3.5 h-3.5" /> Page
          </IconBtn>
        )}

        {/* Divider */}
        <div className="w-px h-5 bg-slate-700 mx-1" />

        {/* Simulate toggle */}
        <button
          onClick={() => { setIsSimulating(s => !s); setSelectedIds([]); }}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold transition-colors ${
            isSimulating
              ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
              : 'bg-slate-700 hover:bg-slate-600 text-slate-300'
          }`}
          title="Toggle simulation mode"
        >
          {isSimulating ? <StopCircle className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
          {isSimulating ? 'Exit Sim' : 'Simulate'}
        </button>

        {/* Shortcut hint */}
        <span className="text-[9px] text-slate-600 ml-auto hidden lg:block">
          Del=delete · Ctrl+D=duplicate · Ctrl+Z=undo · Ctrl+Y=redo · Arrows=1px · Shift+Arrows=10px · Drag empty canvas=multi-select
        </span>
      </div>

      {/* ── Page tabs ── */}
      {clientId && (
        <div className="flex items-center gap-1.5 px-3 py-1.5 border-b border-slate-800 bg-slate-900/60 overflow-x-auto shrink-0">
          <span className="text-[9px] text-slate-500 uppercase tracking-wider font-bold mr-1 shrink-0">Pages</span>

          <IconBtn onClick={() => goToPage(-1)} title="Previous page" disabled={layouts.length === 0 || currentPageIndex <= 0}>
            <ChevronLeft className="w-3.5 h-3.5" />
          </IconBtn>

          {layouts.length > 0 && (
            <span className="text-[9px] text-slate-500 shrink-0 tabular-nums">
              {currentPageIndex === -1 ? '—' : currentPageIndex + 1} / {layouts.length}
            </span>
          )}

          <IconBtn onClick={() => goToPage(1)} title="Next page" disabled={layouts.length === 0 || currentPageIndex === -1 || currentPageIndex === layouts.length - 1}>
            <ChevronRight className="w-3.5 h-3.5" />
          </IconBtn>

          <div className="w-px h-5 bg-slate-700 mx-0.5 shrink-0" />

          {/* Reorders the whole client's page list — this is what decides which
              screen the Client Portal viewer opens 1st, and the order screens are
              listed in a Nav Button's "go to screen" picker. */}
          <IconBtn onClick={() => reorderPage(-1)} title="Move this page earlier" disabled={currentPageIndex <= 0}>
            <ChevronsLeft className="w-3.5 h-3.5" />
          </IconBtn>
          <IconBtn onClick={() => reorderPage(1)} title="Move this page later" disabled={currentPageIndex === -1 || currentPageIndex === layouts.length - 1}>
            <ChevronsRight className="w-3.5 h-3.5" />
          </IconBtn>

          <div className="w-px h-5 bg-slate-700 mx-0.5 shrink-0" />

          {layouts.map(l => (
            <button
              key={l.id}
              onClick={() => loadLayout(l)}
              title={l.name}
              className={`shrink-0 px-3 py-1 rounded text-xs font-semibold transition-colors ${
                l.id === currentId
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white'
              }`}
            >
              {l.name}
            </button>
          ))}
          {currentId === null && (
            <span
              className="shrink-0 px-3 py-1 rounded text-xs font-semibold bg-blue-600/60 text-white border border-dashed border-blue-400"
              title="Not saved yet — click Save to add this page"
            >
              {layoutName || 'New Page'} •
            </span>
          )}
          <IconBtn onClick={newLayout} title="Add a new page">
            <Plus className="w-3.5 h-3.5" /> Add Page
          </IconBtn>
        </div>
      )}

      {/* ── Body ── */}
      <div className="flex flex-1 overflow-hidden">

        {/* ── Palette ── */}
        <div className="w-40 shrink-0 border-r border-slate-800 bg-slate-900 flex flex-col overflow-y-auto">
          <p className="text-[9px] text-slate-500 uppercase tracking-wider px-3 pt-3 pb-1.5 font-bold">Components</p>
          {PALETTE_ITEMS.map(item => (
            <button
              key={item.type}
              onClick={() => addComponent(item.type)}
              className="flex flex-col items-start px-3 py-2 hover:bg-slate-800 text-left border-b border-slate-800/60 transition-colors group"
            >
              <span className="text-xs font-semibold text-slate-200 group-hover:text-white">{item.label}</span>
              <span className="text-[9px] text-slate-500">{item.desc}</span>
            </button>
          ))}
        </div>

        {/* ── Canvas ── */}
        <div className="flex-1 min-h-0 overflow-auto bg-slate-950 p-4 flex items-start justify-center">
          <svg
            ref={svgRef}
            viewBox={`0 0 ${CANVAS_W} ${CANVAS_H}`}
            style={{
              background,
              border: '1px solid #334155',
              borderRadius: 8,
              width: '100%',
              aspectRatio: `${CANVAS_W} / ${CANVAS_H}`,
              maxWidth: '100%',
              display: 'block',
              cursor: dragRef.current ? 'grabbing' : 'default',
              minWidth: 600,
            }}
            onMouseDown={handleSVGMouseDown}
            onMouseMove={handleSVGMouseMove}
            onMouseUp={stopDrag}
            onMouseLeave={stopDrag}
          >
            {/* Grid */}
            <defs>
              <pattern id="hmi-grid-b" width={GRID} height={GRID} patternUnits="userSpaceOnUse">
                <path d={`M ${GRID} 0 L 0 0 0 ${GRID}`} fill="none" stroke="#1e293b" strokeWidth={0.5} />
              </pattern>
            </defs>
            <rect width={CANVAS_W} height={CANVAS_H} fill="url(#hmi-grid-b)" />

            {/* Components */}
            {components.map(comp => {
              const cx = comp.x + comp.width / 2;
              const cy = comp.y + comp.height / 2;
              const rot = comp.rotation ?? 0;

              // Pipes can mirror another component's ON/OFF state instead of their own
              // tag binding (e.g. "only flows when this valve is open") — follows a
              // chain of references (pipe -> pipe -> valve) if there is one. The
              // continuous intensity version lets a Flow Control Valve source make
              // the pipe's flow visibly speed up/brighten with its 0-100% position,
              // instead of just switching the flow on/off.
              const getRawFlowValue = (c: HMIComponent) => isSimulating ? (simValues[c.id] ?? null) : null;
              const flowSourceOn = comp.flowSourceId
                ? resolveFlowSourceOn(comp, components, getRawFlowValue)
                : undefined;
              const flowIntensity = comp.flowSourceId
                ? resolveFlowSourceIntensity(comp, components, getRawFlowValue)
                : undefined;

              return (
                <g
                  key={comp.id}
                  transform={rot !== 0
                    ? `rotate(${rot},${cx},${cy}) translate(${comp.x},${comp.y})`
                    : `translate(${comp.x},${comp.y})`}
                  onMouseDown={e => handleCompMouseDown(e, comp.id)}
                  onClick={e => e.stopPropagation()}
                  style={{ cursor: 'grab', userSelect: 'none' }}
                >
                  {renderHMIComponent(
                    comp,
                    isSimulating ? (simValues[comp.id] ?? null) : null,
                    {
                      onLabelMouseDown: e => handleLabelMouseDown(e, comp.id), selected: labelTargetId === comp.id,
                      onStatusMouseDown: e => handleStatusMouseDown(e, comp.id), statusSelected: statusTargetId === comp.id,
                    },
                    flowSourceOn,
                    flowIntensity
                  )}
                  {/* Selection highlight */}
                  {selectedIds.includes(comp.id) && (
                    <rect
                      x={-4} y={-4}
                      width={comp.width + 8} height={comp.height + 8}
                      fill="none" stroke="#3b82f6" strokeWidth={1.5}
                      strokeDasharray="5 3" rx={4}
                      style={{ pointerEvents: 'none' }}
                    />
                  )}
                  {/* Line/Arrow endpoint handles — drag either end directly instead of
                      juggling Width + Rotation fields; keeps the shape exactly where
                      you point it regardless of angle. */}
                  {selectedIds.length === 1 && selectedIds[0] === comp.id && (comp.type === 'line' || comp.type === 'arrow') && (
                    <>
                      <circle
                        cx={0} cy={comp.height / 2} r={6}
                        fill="#3b82f6" stroke="white" strokeWidth={1.5}
                        style={{ cursor: 'move' }}
                        onMouseDown={e => handleEndpointMouseDown(e, comp.id, 'start')}
                      />
                      <circle
                        cx={comp.width} cy={comp.height / 2} r={6}
                        fill="#3b82f6" stroke="white" strokeWidth={1.5}
                        style={{ cursor: 'move' }}
                        onMouseDown={e => handleEndpointMouseDown(e, comp.id, 'end')}
                      />
                    </>
                  )}
                </g>
              );
            })}

            {/* Marquee drag-select rectangle */}
            {marqueeRect && (
              <rect
                x={marqueeRect.x} y={marqueeRect.y} width={marqueeRect.w} height={marqueeRect.h}
                fill="#3b82f6" fillOpacity={0.12} stroke="#3b82f6" strokeWidth={1} strokeDasharray="4 3"
                style={{ pointerEvents: 'none' }}
              />
            )}
          </svg>
        </div>

        {/* ── Properties / Simulation panel ── */}
        <div className="w-60 shrink-0 border-l border-slate-800 bg-slate-900 overflow-y-auto">
          {isSimulating ? (
            <SimulationPanel
              components={components}
              simValues={simValues}
              onValueChange={(id, val) => setSimValues(prev => ({ ...prev, [id]: val }))}
              onReset={() => setSimValues({})}
            />
          ) : selectedIds.length > 1 ? (
            <MultiSelectPanel
              count={selectedIds.length}
              onDelete={deleteSelected}
              onDuplicate={duplicateSelected}
              onRotateCW={rotateCW}
              onRotateCCW={rotateCCW}
              onColorChange={bulkSetColor}
              onShellColorChange={bulkSetShellColor}
              onSetSize={bulkSetSize}
              onScale={bulkScale}
              onClear={() => setSelectedIds([])}
            />
          ) : selectedComp ? (
            <PropsPanel
              key={selectedComp.id}
              comp={selectedComp}
              components={components}
              availableTags={availableTags}
              otherScreens={layouts.filter(l => l.id !== currentId).map(l => ({ id: l.id, name: l.name }))}
              onChange={updateProp}
              onDelete={deleteSelected}
              onDuplicate={duplicateSelected}
              onRotateCW={rotateCW}
              onRotateCCW={rotateCCW}
              onBringToFront={bringToFront}
              onSendToBack={sendToBack}
              onBringForward={bringForward}
              onSendBackward={sendBackward}
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-slate-600 text-xs gap-3 p-6 text-center">
              <Monitor className="w-10 h-10 opacity-20" />
              <p className="font-semibold text-slate-500">No component selected</p>
              <p className="text-[10px] text-slate-600">Click a component to edit it, drag on empty canvas to select multiple, or add one from the left panel.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
